import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, Mail, Lock, AlertCircle, Loader2 } from 'lucide-react';
import { seedDemoData } from '../lib/api';
import { toast } from 'sonner';
import DomioLogo from '../components/common/DomioLogo';
import { useI18n } from '../i18n';
import { isTenantUser } from '../lib/authRoles';
import { validateEmailAddress } from '../lib/emailValidation';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const { login, error } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const checkEmail = (value = email) => {
    const result = validateEmailAddress(value);
    if (!result.valid) {
      setEmailError(result.reason === 'required' ? t('auth.emailRequired') : t('auth.invalidEmail'));
      return null;
    }
    setEmailError('');
    return result.email;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalizedEmail = checkEmail();
    if (!normalizedEmail) return;

    setIsLoading(true);
    const result = await login(normalizedEmail, password);
    
    if (result.success) {
      const tenant = isTenantUser(result.user);
      if (result.user?.must_change_password) {
        navigate(tenant ? '/app/tenant' : '/app/settings');
      } else if (tenant) {
        navigate('/app/tenant');
      } else {
        navigate('/app/dashboard');
      }
    }
    setIsLoading(false);
  };

  const handleSeedDemo = async () => {
    setSeedingDemo(true);
    try {
      const response = await seedDemoData();
      const { demo_email, demo_password } = response.data;
      setEmail(demo_email);
      setPassword(demo_password);
      toast.success(t('auth.demoCreated'));
    } catch (err) {
      if (err.response?.data?.message?.includes('already exists')) {
        setEmail('demo@propertymgmt.com');
        setPassword('Demo1234');
        toast.info(t('auth.demoReady'));
      } else {
        toast.error(t('auth.demoCreateFailed'));
      }
    }
    setSeedingDemo(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1664813953897-ada06817c48c?w=1200&q=80)'
        }}
      >
        <div className="absolute inset-0 bg-[#071E35]/70" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h2 className="font-manrope text-4xl font-bold text-white mb-4">
            {t('auth.smartPropertyManagement')}
          </h2>
          <p className="text-slate-300 text-lg max-w-md">
            {t('auth.platformDescription')}
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <DomioLogo size="xl" showText textClassName="text-slate-900" />
          </div>

          <h1 className="font-manrope text-3xl font-bold text-slate-900 mb-2">
            {t('auth.welcomeBack')}
          </h1>
          <p className="text-slate-500 mb-8">
            {t('auth.signInToContinue')}
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-sm flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-domio-600 flex-shrink-0" />
              <p className="text-sm text-domio-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">
                {t('auth.email')}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError('');
                  }}
                  onBlur={() => email.trim() && checkEmail()}
                  placeholder={t('auth.enterEmail')}
                  className={`pl-10 h-11 bg-white border-slate-200 focus:border-domio-600 focus:ring-domio-600/20 ${emailError ? 'border-red-400 focus:border-red-500' : ''}`}
                  aria-invalid={Boolean(emailError)}
                  aria-describedby={emailError ? 'email-error' : undefined}
                  required
                  data-testid="email-input"
                />
              </div>
              {emailError && (
                <p id="email-error" className="text-sm text-red-600" role="alert">
                  {emailError}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-700 font-medium">
                  {t('auth.password')}
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-domio-600 hover:text-domio-700 font-medium"
                  data-testid="forgot-password-link"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.enterPassword')}
                  className="pl-10 h-11 bg-white border-slate-200 focus:border-domio-600 focus:ring-domio-600/20"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-domio-600 hover:bg-domio-700 text-white font-semibold rounded-sm"
              disabled={isLoading}
              data-testid="login-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('auth.signingIn')}
                </>
              ) : (
                t('auth.login')
              )}
            </Button>
          </form>

          <div className="mt-6">
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 border-slate-200 text-slate-700 hover:bg-slate-100 rounded-sm"
              onClick={handleSeedDemo}
              disabled={seedingDemo}
              data-testid="demo-btn"
            >
              {seedingDemo ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('auth.creatingAccount')}
                </>
              ) : (
                t('auth.tryDemoAccount')
              )}
            </Button>
          </div>

          <p className="mt-8 text-center text-slate-500">
            {t('auth.dontHaveAccount')}{' '}
            <Link to="/register" className="text-domio-600 hover:text-domio-700 font-medium">
              {t('auth.register')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
